import { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Card, Checkbox, FormControlLabel, FormGroup, Rating } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FaCheck, FaClipboardList, FaExclamationTriangle, FaRedo } from "react-icons/fa";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import OverlayLoader from "components/OverlayLoader";

import useCommon from "hooks/useCommon";

import {
  resetSubmitSatisfactionFormState,
  resetValidateSatisfactionReservationState,
  submitSatisfactionForm,
  validateSatisfactionReservation,
} from "store/slice/satisfactionSlice";

import colors from "assets/theme/base/colors";
import borders from "assets/theme/base/borders";
import rgba from "assets/theme/functions/rgba";

const { black, dark, primary, success, error, light, warning } = colors;

const { borderWidth } = borders;

const getQuestionId = (question) => question?.questionId || question?.id || question?._id;

const getQuestionText = (question) => question?.questionText || question?.question || "";

const getQuestionType = (question) => {
  const type = question?.questionType || question?.type || "";

  if (type === "rating_scale") {
    return "rate_scale";
  }

  if (type === "long_text") {
    return "text_input";
  }

  return type;
};

const getOptionId = (option) => {
  if (typeof option === "string") {
    return option;
  }

  return option?.optionId || option?.id || option?._id || option?.label;
};

const getOptionLabel = (option) => {
  if (typeof option === "string") {
    return option;
  }

  return option?.label || "";
};

const getApiErrorMessage = (apiError, fallbackMessage) => {
  if (!apiError) {
    return fallbackMessage;
  }

  if (typeof apiError === "string") {
    return apiError;
  }

  return apiError?.message || apiError?.response?.data?.message || fallbackMessage;
};

const StatusState = ({ icon, title, description, actionLabel, onAction, type = "error" }) => {
  const stateColor =
    type === "success" ? success.main : type === "warning" ? warning.main : error.main;

  return (
    <VuiBox
      minHeight="100vh"
      px={2}
      py={4}
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{
        background: light.secondary,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 540,
          p: {
            xs: 2.5,
            md: 4,
          },
          borderRadius: "24px",
          textAlign: "center",
          background: light.main,
          border: `${borderWidth[1]} solid ${rgba(black.main, 0.1)}`,
          boxShadow: "none",
        }}
      >
        <VuiBox
          width={58}
          height={58}
          borderRadius="18px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          mx="auto"
          mb={2}
          sx={{
            background: rgba(stateColor, 0.1),
            border: `${borderWidth[1]} solid ${rgba(stateColor, 0.2)}`,
            color: stateColor,
          }}
        >
          {icon}
        </VuiBox>

        <VuiTypography variant="h4" color="dark" fontWeight="regular" mb={1}>
          {title}
        </VuiTypography>

        <VuiTypography
          variant="button"
          color="dark"
          fontWeight="regular"
          sx={{
            display: "block",
            maxWidth: "420px",
            mx: "auto",
            opacity: 0.72,
            lineHeight: 1.7,
          }}
        >
          {description}
        </VuiTypography>

        {actionLabel && onAction && (
          <VuiBox mt={3}>
            <VuiButton color="primary" onClick={onAction} startIcon={<FaRedo size={12} />}>
              {actionLabel}
            </VuiButton>
          </VuiBox>
        )}
      </Card>
    </VuiBox>
  );
};

const GuestSatisfactionFormSubmission = () => {
  const { DISPATCH, STORE } = useCommon();
  const [searchParams] = useSearchParams();

  const reservationId = searchParams.get("reservationId") || searchParams.get("rid") || "";

  const {
    publicValidationResult,
    validateReservationLoading,
    validateReservationError,

    submissionResult,
    submitFormLoading,
    submitFormSuccess,
    submitFormError,
  } = STORE((state) => state.satisfaction);

  const [answers, setAnswers] = useState({});
  const [errors, setErrors] = useState({});
  const [pageError, setPageError] = useState("");

  const questionRefs = useRef({});

  const form = publicValidationResult?.form || null;

  const questions = useMemo(() => {
    const formQuestions = Array.isArray(form?.questions) ? form.questions : [];

    return [...formQuestions]
      .filter((question) => question?.isActive !== false)
      .sort((first, second) => Number(first?.sortOrder || 0) - Number(second?.sortOrder || 0));
  }, [form]);

  const totalQuestions = questions.length;

  const hasAnswer = (question, value) => {
    const questionType = getQuestionType(question);

    if (questionType === "rate_scale") {
      const ratingValue = Number(value);

      return ratingValue >= 1 && ratingValue <= 5;
    }

    if (questionType === "multiple_choice") {
      return Array.isArray(value) && value.length > 0;
    }

    if (questionType === "text_input") {
      return String(value || "").trim().length > 0;
    }

    return String(value || "").trim().length > 0;
  };

  const answeredCount = useMemo(() => {
    return questions.filter((question) => {
      const questionId = getQuestionId(question);

      const questionType = getQuestionType(question);

      const value = answers[questionId];

      if (questionType === "rate_scale") {
        return Number(value) >= 1 && Number(value) <= 5;
      }

      if (questionType === "multiple_choice") {
        return Array.isArray(value) && value.length > 0;
      }

      return String(value || "").trim().length > 0;
    }).length;
  }, [answers, questions]);

  const completionPercent =
    totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const loadForm = async () => {
    if (!reservationId) {
      return;
    }

    setAnswers({});
    setErrors({});
    setPageError("");

    DISPATCH(resetValidateSatisfactionReservationState());
    DISPATCH(resetSubmitSatisfactionFormState());
    try {
      await DISPATCH(validateSatisfactionReservation(reservationId)).unwrap();
    } catch (requestError) {
      setPageError(getApiErrorMessage(requestError, "This feedback form is unavailable."));
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers((current) => ({
      ...current,
      [questionId]: value,
    }));

    setErrors((current) => ({
      ...current,
      [questionId]: null,
    }));

    if (pageError) {
      setPageError("");
    }
  };

  const validate = () => {
    const nextErrors = {};

    const answeredQuestions = questions.filter((question) => {
      const questionId = getQuestionId(question);
      const value = answers[questionId];

      return hasAnswer(question, value);
    });

    if (answeredQuestions.length === 0) {
      setErrors({});
      setPageError("Please answer at least one question before submitting.");

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });

      return false;
    }

    answeredQuestions.forEach((question) => {
      const questionId = getQuestionId(question);
      const questionType = getQuestionType(question);
      const value = answers[questionId];

      if (questionType === "rate_scale") {
        const ratingValue = Number(value);

        if (ratingValue < 1 || ratingValue > 5) {
          nextErrors[questionId] = "Please select a rating from 1 to 5.";
        }

        return;
      }

      if (questionType === "multiple_choice") {
        if (!Array.isArray(value) || value.length === 0) {
          nextErrors[questionId] = "Please select at least one option.";
        }

        return;
      }

      if (questionType === "text_input") {
        const textValue = String(value || "").trim();

        if (textValue.length < 2) {
          nextErrors[questionId] = "Your response is too short.";
        } else if (textValue.length > 2000) {
          nextErrors[questionId] = "Your response cannot exceed 2,000 characters.";
        }
      }
    });

    setErrors(nextErrors);

    const firstErrorQuestionId = Object.keys(nextErrors)[0];

    if (firstErrorQuestionId) {
      setPageError("Please correct the highlighted response before submitting.");

      window.requestAnimationFrame(() => {
        questionRefs.current[firstErrorQuestionId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });

      return false;
    }

    setPageError("");

    return true;
  };

  const buildSubmissionAnswers = () =>
    questions
      .filter((question) => {
        const questionId = getQuestionId(question);

        return hasAnswer(question, answers[questionId]);
      })
      .map((question) => {
        const questionId = getQuestionId(question);
        const questionType = getQuestionType(question);
        const rawAnswer = answers[questionId];

        if (questionType === "rate_scale") {
          return {
            questionId,
            questionType,
            value: Number(rawAnswer),
          };
        }

        if (questionType === "multiple_choice") {
          return {
            questionId,
            questionType,
            value: rawAnswer,
          };
        }

        return {
          questionId,
          questionType,
          value: String(rawAnswer).trim(),
        };
      });

  const handleSubmit = async () => {
    if (submitFormLoading || !form || !publicValidationResult) {
      return;
    }

    if (!validate()) {
      return;
    }

    const payload = {
      reservationId: publicValidationResult?.reservationId || reservationId,
      answers: buildSubmissionAnswers(),
    };

    try {
      await DISPATCH(submitSatisfactionForm(payload)).unwrap();

      setErrors({});
      setPageError("");
    } catch (requestError) {
      setPageError(
        getApiErrorMessage(
          requestError,
          "We could not submit your feedback. Please review your responses and try again."
        )
      );

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    if (!reservationId) {
      return;
    }
    loadForm();
    return () => {
      DISPATCH(resetValidateSatisfactionReservationState());

      DISPATCH(resetSubmitSatisfactionFormState());
    };
  }, [DISPATCH, reservationId]);

  if (!reservationId) {
    return (
      <StatusState
        icon={<FaExclamationTriangle size={22} />}
        title="Invalid Feedback Link"
        description="The feedback link is missing a reservation ID. Please use the original link sent by the hotel."
      />
    );
  }

  if (validateReservationLoading && !publicValidationResult) {
    return (
      <VuiBox
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        sx={{
          background: light.secondary,
        }}
      >
        <OverlayLoader
          open
          message="Validating your reservation and loading the feedback form..."
        />
      </VuiBox>
    );
  }

  if (!validateReservationLoading && (validateReservationError || pageError) && !form) {
    const validationMessage = getApiErrorMessage(
      validateReservationError || pageError,
      "This feedback form is unavailable."
    );

    const normalizedMessage = validationMessage.toLowerCase();

    const alreadySubmitted =
      normalizedMessage.includes("already submitted") ||
      normalizedMessage.includes("already been submitted");

    const cancelledOrNoShow =
      normalizedMessage.includes("cancelled") ||
      normalizedMessage.includes("canceled") ||
      normalizedMessage.includes("no-show") ||
      normalizedMessage.includes("no show");

    if (alreadySubmitted) {
      return (
        <StatusState
          icon={<FaCheck size={22} />}
          title="Feedback Already Submitted"
          description="Feedback has already been submitted for this reservation. Thank you for sharing your experience."
          type="success"
        />
      );
    }

    if (cancelledOrNoShow) {
      return (
        <StatusState
          icon={<FaExclamationTriangle size={22} />}
          title="Feedback Form Unavailable"
          description="This reservation is not eligible for feedback because it was cancelled or marked as a no-show."
          type="warning"
        />
      );
    }

    return (
      <StatusState
        icon={<FaExclamationTriangle size={22} />}
        title="Unable to Load Feedback Form"
        description={validationMessage}
        actionLabel="Try Again"
        onAction={loadForm}
      />
    );
  }

  if (!validateReservationLoading && publicValidationResult && !form) {
    return (
      <StatusState
        icon={<FaClipboardList size={22} />}
        title="No Active Feedback Form"
        description="There is currently no active satisfaction form available for this reservation."
        type="warning"
      />
    );
  }

  if (!validateReservationLoading && form && totalQuestions === 0) {
    return (
      <StatusState
        icon={<FaClipboardList size={22} />}
        title="No Questions Available"
        description="The feedback form does not currently contain any active questions."
        type="warning"
      />
    );
  }

  if (submitFormSuccess || submissionResult) {
    return (
      <StatusState
        icon={<FaCheck size={22} />}
        title="Thank You!"
        description="Your feedback has been submitted successfully. Your responses will help improve future guest experiences."
        type="success"
      />
    );
  }

  return (
    <VuiBox
      minHeight="100vh"
      py={{
        xs: 2,
        md: 4,
      }}
      px={{
        xs: 1.5,
        md: 2,
      }}
      display="flex"
      justifyContent="center"
      sx={{
        background: light.secondary,
      }}
    >
      <Card
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 760,
          p: {
            xs: 2,
            md: 3,
          },
          borderRadius: "24px",
          background: light.main,
          border: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
          boxShadow: "none",
          overflow: "hidden",
        }}
      >
        <OverlayLoader open={submitFormLoading} message="Submitting your feedback..." />

        <VuiBox mb={3} textAlign="center" display="flex" flexDirection="column" alignItems="center">
          <VuiTypography variant="h3" color="dark" fontWeight="medium">
            {form?.name || "Guest Satisfaction Form"}
          </VuiTypography>

          <VuiTypography
            display="block"
            variant="button"
            color="dark"
            fontWeight="regular"
            sx={{
              mt: 0.8,
              opacity: 0.75,
              maxWidth: 560,
              mx: "auto",
              lineHeight: 1.6,
            }}
          >
            {form?.description || "Please tell us about your stay."}
          </VuiTypography>
        </VuiBox>

        {pageError && (
          <Alert
            severity="error"
            variant="light"
            sx={{
              mb: 2.5,
              borderRadius: "12px",
            }}
          >
            {pageError}
          </Alert>
        )}

        {submitFormError && !pageError && (
          <Alert
            severity="error"
            variant="light"
            sx={{
              mb: 2.5,
              borderRadius: "12px",
            }}
          >
            {getApiErrorMessage(submitFormError, "Unable to submit feedback.")}
          </Alert>
        )}

        <VuiBox mb={3}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.8}>
            <VuiTypography variant="caption" color="dark" fontWeight="bold">
              Completion
            </VuiTypography>

            <VuiTypography variant="caption" color="dark" fontWeight="bold">
              {answeredCount}/{totalQuestions} answered
            </VuiTypography>
          </VuiBox>

          <VuiBox
            width="100%"
            height="8px"
            borderRadius="20px"
            sx={{
              background: rgba(black.main, 0.08),
              overflow: "hidden",
            }}
          >
            <VuiBox
              height="100%"
              width={`${completionPercent}%`}
              borderRadius="20px"
              sx={{
                background: primary.main,
                transition: "width 0.25s ease",
              }}
            />
          </VuiBox>
        </VuiBox>

        <VuiBox display="flex" flexDirection="column" gap={2}>
          {questions.map((question, index) => {
            const questionId = getQuestionId(question);

            const questionText = getQuestionText(question);

            const questionType = getQuestionType(question);

            const options = Array.isArray(question?.options)
              ? question.options.filter(
                  (option) => typeof option === "string" || option?.isActive !== false
                )
              : [];

            const isRequired = question?.isRequired !== false;

            return (
              <VuiBox
                key={questionId || index}
                ref={(element) => {
                  if (questionId) {
                    questionRefs.current[questionId] = element;
                  }
                }}
                p={2}
                borderRadius="18px"
                sx={{
                  border: `${borderWidth[1]} solid ${
                    errors[questionId] ? rgba(error.main, 0.35) : rgba(black.main, 0.18)
                  }`,
                  transition: "border-color 0.2s ease",
                }}
              >
                <VuiBox display="flex" gap={1} alignItems="flex-start" mb={1.4}>
                  <VuiTypography variant="caption" color="dark" fontWeight="bold">
                    {index + 1}.
                  </VuiTypography>

                  <VuiTypography variant="button" color="dark" fontWeight="medium">
                    {questionText}

                    {/* {isRequired && (
                      <VuiTypography
                        component="span"
                        variant="button"
                        color="error"
                        sx={{ ml: 0.4 }}
                      >
                        *
                      </VuiTypography>
                    )} */}
                  </VuiTypography>
                </VuiBox>

                {questionType === "rate_scale" && (
                  <VuiBox pl={2.4}>
                    <Rating
                      value={Number(answers[questionId] || 0)}
                      onChange={(_, value) => handleAnswerChange(questionId, value)}
                      max={5}
                      size="large"
                      disabled={submitFormLoading}
                      sx={{
                        color: warning.main,

                        "& .MuiRating-iconEmpty": {
                          color: rgba(dark.main, 0.25),
                        },
                      }}
                    />

                    <VuiTypography
                      display="block"
                      variant="caption"
                      color="dark"
                      fontWeight="regular"
                      sx={{
                        mt: 0.6,
                        opacity: 0.65,
                      }}
                    >
                      {answers[questionId] ? `${answers[questionId]} out of 5` : "Select a rating"}
                    </VuiTypography>
                  </VuiBox>
                )}

                {questionType === "multiple_choice" && (
                  <>
                    {options.length > 0 ? (
                      <FormGroup sx={{ pl: 1 }}>
                        {options.map((option, optionIndex) => {
                          const optionId = getOptionId(option);
                          const optionLabel = getOptionLabel(option);

                          const selectedOptions = Array.isArray(answers[questionId])
                            ? answers[questionId]
                            : [];

                          const checked = selectedOptions.includes(optionId);

                          return (
                            <FormControlLabel
                              key={optionId || optionIndex}
                              disabled={submitFormLoading}
                              control={
                                <Checkbox
                                  checked={checked}
                                  onChange={(event) => {
                                    const updatedOptions = event.target.checked
                                      ? [...new Set([...selectedOptions, optionId])]
                                      : selectedOptions.filter(
                                          (selectedOptionId) => selectedOptionId !== optionId
                                        );

                                    handleAnswerChange(questionId, updatedOptions);
                                  }}
                                  sx={{
                                    color: rgba(dark.main, 0.45),

                                    "&.Mui-checked": {
                                      color: primary.main,
                                    },
                                  }}
                                />
                              }
                              label={
                                <VuiTypography variant="caption" color="dark" fontWeight="medium">
                                  {optionLabel}
                                </VuiTypography>
                              }
                            />
                          );
                        })}
                      </FormGroup>
                    ) : (
                      <Alert severity="warning" variant="light">
                        No answer options are available for this question.
                      </Alert>
                    )}
                  </>
                )}
                {questionType === "text_input" && (
                  <>
                    <VuiInput
                      gradient
                      fullWidth
                      multiline
                      rows={question?.multiline ? 4 : 3}
                      placeholder="Write your answer..."
                      value={answers[questionId] || ""}
                      onChange={(event) => handleAnswerChange(questionId, event.target.value)}
                      disabled={submitFormLoading}
                      inputProps={{
                        maxLength: 300,
                      }}
                    />

                    <VuiBox mt={0.4} display="flex" justifyContent="flex-end">
                      <VuiTypography
                        variant="caption"
                        color="dark"
                        sx={{
                          opacity: 0.55,
                        }}
                      >
                        {String(answers[questionId] || "").length}
                        /300
                      </VuiTypography>
                    </VuiBox>
                  </>
                )}

                {!["rate_scale", "multiple_choice", "text_input"].includes(questionType) && (
                  <Alert severity="warning" variant="light">
                    This question type is not currently supported.
                  </Alert>
                )}

                {errors[questionId] && (
                  <VuiTypography
                    display="block"
                    variant="caption"
                    color="error"
                    fontWeight="medium"
                    sx={{ mt: 1 }}
                  >
                    {errors[questionId]}
                  </VuiTypography>
                )}
              </VuiBox>
            );
          })}
        </VuiBox>

        <VuiBox
          mt={3}
          pt={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexWrap="wrap"
          sx={{
            borderTop: `${borderWidth[1]} solid ${rgba(black.main, 0.08)}`,
          }}
        >
          <VuiTypography
            variant="caption"
            color="dark"
            fontWeight="regular"
            sx={{
              opacity: 0.7,
              maxWidth: "430px",
              lineHeight: 1.6,
            }}
          >
            Your responses help us improve future guest experiences.
          </VuiTypography>

          <VuiButton
            color="primary"
            onClick={handleSubmit}
            disabled={submitFormLoading || totalQuestions === 0}
          >
            Submit Feedback
          </VuiButton>
        </VuiBox>
      </Card>
    </VuiBox>
  );
};

export default GuestSatisfactionFormSubmission;
